import type { ReactNode } from 'react';
import { ThemeToggle } from '../theme/ThemeToggle';

/**
 * Shared dark-theme shell for the public auth pages (forgot/reset/verify/invite/register).
 * Visual clone of the LoginPage layout: #0A0E1A background, dual radial Firmio glows
 * (top-left blue + bottom-right purple), dot-pattern overlay, gradient wordmark and
 * the #0F1626 card.
 */
export function AuthShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden p-4"
      style={{ background: '#0A0E1A' }}
    >
      {/* Dual radial gradient blobs — top-left blue + bottom-right purple. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 1100px 700px at 15% 20%, rgba(59, 130, 246, 0.15), transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 900px 900px at 85% 80%, rgba(139, 92, 246, 0.12), transparent 70%)',
        }}
      />

      {/* Dot-pattern overlay. */}
      <div aria-hidden className="auth-dots pointer-events-none absolute inset-0" />

      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <div className="relative z-10 w-full max-w-sm animate-[ui-fade-in_400ms_ease-out]">
        <h1 className="mb-6 flex justify-center">
          <img
            src="/brand/firmio-logo-horizontal-on-dark.svg"
            alt="Firmio"
            className="h-16 w-auto"
          />
        </h1>

        <div
          className="space-y-4 rounded-2xl p-6"
          style={{
            background: '#0F1626',
            border: '1px solid rgba(148, 163, 184, 0.12)',
            boxShadow: '0 20px 50px -15px rgba(99, 102, 241, 0.3)',
          }}
        >
          <h2
            className="text-2xl font-bold"
            style={{ color: '#F1F5F9', fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            {title}
          </h2>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Red error banner matching the LoginPage styling. */
export function AuthError({ message }: { message: string }) {
  return (
    <p
      className="rounded-[10px] px-3 py-2 text-sm"
      role="alert"
      style={{
        background: 'rgba(248, 113, 113, 0.12)',
        color: '#fca5a5',
        border: '1px solid rgba(248, 113, 113, 0.25)',
      }}
    >
      {message}
    </p>
  );
}

/** Green success banner (counterpart of AuthError). */
export function AuthSuccess({ message }: { message: string }) {
  return (
    <p
      className="rounded-[10px] px-3 py-2 text-sm"
      role="status"
      style={{
        background: 'rgba(74, 222, 128, 0.12)',
        color: '#86efac',
        border: '1px solid rgba(74, 222, 128, 0.25)',
      }}
    >
      {message}
    </p>
  );
}
